/**
 * Local cart — persisted in localStorage.
 * Cart มีอายุการใช้งานเฉพาะตอนเลือกสินค้า
 * ก่อน redirect ไป Shopify checkout (ที่ Shopify เป็น authoritative)
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { CartItem } from '@meso/types';

const KEY = 'meso_cart_v1';
const EMPTY: CartItem[] = [];

let listeners: Array<() => void> = [];
function emit() { listeners.forEach((l) => l()); }

// useSyncExternalStore ต้องได้ snapshot reference เดิม
// ถ้า data ไม่เปลี่ยน ไม่งั้น React จะ infinite re-render
let cachedRaw = '__INIT__';
let cachedItems: CartItem[] = EMPTY;

function read(): CartItem[] {
  let raw: string;
  try {
    raw = localStorage.getItem(KEY) || '';
  } catch {
    return EMPTY;
  }
  if (raw === cachedRaw) return cachedItems;
  cachedRaw = raw;
  try {
    cachedItems = raw ? (JSON.parse(raw) as CartItem[]) : EMPTY;
  } catch {
    cachedItems = EMPTY;
  }
  return cachedItems;
}

function write(items: CartItem[]) {
  try {
    const json = JSON.stringify(items);
    localStorage.setItem(KEY, json);
    cachedRaw = json;
    cachedItems = items;
    emit();
  } catch {/* ignore */}
}

const subscribe = (l: () => void) => {
  listeners.push(l);
  return () => { listeners = listeners.filter((x) => x !== l); };
};

const getServerSnapshot = () => EMPTY;

export function useCart() {
  const items = useSyncExternalStore(subscribe, read, getServerSnapshot);

  const add = useCallback((item: CartItem) => {
    const cur = read().slice();
    const idx = cur.findIndex((i) => i.variantId === item.variantId);
    if (idx >= 0) {
      cur[idx] = { ...cur[idx]!, quantity: cur[idx]!.quantity + item.quantity };
    } else {
      cur.push(item);
    }
    write(cur);
  }, []);

  const updateQty = useCallback((variantId: string, qty: number) => {
    const cur = read().slice();
    if (qty <= 0) {
      write(cur.filter((i) => i.variantId !== variantId));
      return;
    }
    const next = cur.map((i) => i.variantId === variantId ? { ...i, quantity: qty } : i);
    write(next);
  }, []);

  const remove = useCallback((variantId: string) => {
    write(read().filter((i) => i.variantId !== variantId));
  }, []);

  const clear = useCallback(() => write(EMPTY), []);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return { items, add, updateQty, remove, clear, total, count };
}
